/**
 * Backend Service Configuration
 * 
 * This module provides configuration for backend service URLs.
 * In K8s, services communicate via internal cluster DNS.
 * In development/Cloud Run, they use external URLs.
 * 
 * Server-side code uses internal URLs (env vars without NEXT_PUBLIC_ prefix).
 * Client-side code should call Next.js API routes which proxy to backends.
 */

/**
 * Get the base URL for a backend service.
 * Uses internal K8s DNS when running in cluster, external URLs otherwise.
 */
export function requireAnyEnv(service: string, envVars: string[]): string {
  for (const envName of envVars) {
    const value = process.env[envName];
    if (typeof value === 'string' && value.trim().length > 0) {
      return value.trim();
    }
  }

  throw new Error(
    `[config/services] Missing URL for ${service}. Set one of: ${envVars.join(', ')}`
  );
}

export function getServiceUrl(service: 'commerce' | 'chat' | 'proximity' | 'websocket' | 'verification' | 'realtime'): string {
  switch (service) {
    case 'commerce':
      return requireAnyEnv('commerce', ['COMMERCE_API_URL', 'NEXT_PUBLIC_COMMERCE_API_URL', 'NEXT_PUBLIC_API_BASE_URL']);
    case 'chat':
      return requireAnyEnv('chat', ['CHAT_API_URL', 'NEXT_PUBLIC_CHAT_API_URL', 'NEXT_PUBLIC_API_BASE_URL']);
    case 'proximity':
      return requireAnyEnv('proximity', ['PROXIMITY_API_URL', 'NEXT_PUBLIC_PROXIMITY_API_URL', 'NEXT_PUBLIC_API_BASE_URL']);
    case 'websocket':
      return requireAnyEnv('websocket', ['WEBSOCKET_URL', 'NEXT_PUBLIC_WEBSOCKET_URL']);
    case 'verification':
      return requireAnyEnv('verification', ['VERIFICATION_API_URL', 'NEXT_PUBLIC_VERIFICATION_API_URL', 'NEXT_PUBLIC_API_BASE_URL']);
    case 'realtime':
      return requireAnyEnv('realtime', ['REALTIME_API_URL', 'NEXT_PUBLIC_REALTIME_API_URL', 'NEXT_PUBLIC_TRACKING_API_URL', 'NEXT_PUBLIC_API_BASE_URL']);
    default:
      throw new Error(`Unsupported service: ${String(service)}`);
  }
}

/**
 * Check if we're running in a K8s cluster (server-side only).
 * K8s sets KUBERNETES_SERVICE_HOST environment variable.
 */
export function isRunningInK8s(): boolean {
  return !!process.env.KUBERNETES_SERVICE_HOST;
}

/**
 * Service URL configuration for documentation/debugging.
 */
export const serviceConfig = {
  /**
   * Internal K8s cluster DNS patterns (namespace varies by environment):
   * - Dev: erunna-backend-dev
   * - Prod: erunna-backend-prod
   * 
   * Pattern: http://<service-name>.erunna-backend-{env}.svc.cluster.local:8080
   * 
   * Examples (dev environment):
   * - commerce: http://commerce-service.erunna-backend-dev.svc.cluster.local:8080
   * - chat: http://comm-service.erunna-backend-dev.svc.cluster.local:8080
   * - proximity: http://proximity-matching.erunna-backend-dev.svc.cluster.local:8080
   * - websocket: ws://websocket-gateway.erunna-backend-dev.svc.cluster.local:8080
   * - verification: http://verification-service.erunna-backend-dev.svc.cluster.local:8080
   * - realtime: http://real-time-tracking.erunna-backend-dev.svc.cluster.local:8080
   */
  internalDnsPattern: {
    commerce: 'http://commerce-service.erunna-backend-{env}.svc.cluster.local:8080',
    chat: 'http://comm-service.erunna-backend-{env}.svc.cluster.local:8080',
    proximity: 'http://proximity-matching.erunna-backend-{env}.svc.cluster.local:8080',
    websocket: 'ws://websocket-gateway.erunna-backend-{env}.svc.cluster.local:8080',
    verification: 'http://verification-service.erunna-backend-{env}.svc.cluster.local:8080',
    realtime: 'http://real-time-tracking.erunna-backend-{env}.svc.cluster.local:8080',
  },
};
